/**
 * PromoStork's own social profiles as icon links (web plan W3.7): in the
 * footer on every page and on the Contact page. Renders nothing while the
 * owner has confirmed no profile (`SITE_SOCIAL_PROFILES`, @/content/site).
 *
 * The footer is in the entry chunk, so the icons and the URL parsing
 * (./SocialIconList, @/utils/siteProfiles) load on demand, only once there is
 * a profile to show; the placeholder keeps their row's height meanwhile, so
 * nothing shifts when they arrive.
 */
import { lazy, Suspense } from 'react';
import { SITE_SOCIAL_PROFILES } from '@/content/site';

// A failed chunk fetch (a network glitch, or a deploy between the entry chunk
// and this one) degrades to no icons: React.lazy would otherwise throw during
// render, and with no error boundary above the footer React unmounts the whole
// page for a decorative row.
const SocialIconList = lazy(() =>
  import('./SocialIconList').catch(() => ({ default: () => null })),
);

export function SiteSocialLinks({
  profiles = SITE_SOCIAL_PROFILES,
  className = '',
}: {
  /** The profile URLs; the owner's by default. */
  profiles?: readonly string[];
  className?: string;
}) {
  // Only http(s) URLs can become links (SocialIconList drops the rest).
  if (!profiles.some((url) => /^https?:\/\//i.test(url.trim()))) return null;
  return (
    <Suspense fallback={<div className={`h-10 ${className}`.trim()} aria-hidden="true" />}>
      <SocialIconList profiles={profiles} className={className} />
    </Suspense>
  );
}

export default SiteSocialLinks;
